import React, { useCallback, useEffect } from "react";
import SimpleLayer7CriteriaSelector from "../../modules/SimpleLayer7CriteriaSelector";
import { MultiGroupSearchSelector } from "../GroupSearch/MultiGroupSearchSelector";
import { useFeatureFlagStore } from "../../storez/FeatureFlagStore";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  InlineNotification,
  Link,
  MultiSearchSelector,
  Option,
  SingleSearchSelector,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import { AgentConfig, FilteringMode, MitmInclusions, MitmObject, NetworkType } from "../../types/AgentConfig";
import { ResponseError } from "../../types/Api";
import { useAgentConfigStore } from "../../storez/AgentConfigStore";
import CardBasedPage from "../templates/CardBasedPage";
import { SectionHeading } from "../shared/SectionHeading";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { useObjectStore } from "../../storez/ObjectStore";
import { ObjectPool } from "../../types/Objects";

const MobileAgentConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const { fetch, getDomainsObjects } = useObjectStore();

  const [loaded, setLoaded] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [changes, setChanges] = React.useState<boolean>(false);

  const [onNetworkConfigValid, setOnNetworkConfigValid] = React.useState<boolean>(true);
  const [offNetworkConfigValid, setOffNetworkConfigValid] = React.useState<boolean>(true);
  const [availableDomainsObjects, setAvailableDomainsObjects] = React.useState<ObjectPool[]>(getDomainsObjects());

  const supportAdmin = localStorage.getItem("SUPPORTADMIN") === "true";

  const { errorToast } = useToast();
  const [featureFlags, getOrFetchFeatureFlags] = useFeatureFlagStore(useCallback((state) => [state.flags, state.getOrFetch] as const, []));
  const [storedAgentConfig, fetchAgentConfig, setAgentConfig, saveAgentConfig] = useAgentConfigStore(
    useCallback((state) => [state.config, state.fetch, state.set, state.save] as const, [])
  );
  const [networkConfig, setNetworkConfig] = React.useState<AgentConfig>(storedAgentConfig);

  useEffect(() => {
    void Promise.all([fetchAgentConfig(), getOrFetchFeatureFlags()])
      .then(([agentConfig]) => {
        setNetworkConfig(agentConfig);
        setLoaded(true);
      })
      .catch((err: ResponseError) => {
        errorToast({
          title: t("Please try again"),
          description: t(err.message),
          isClosable: true,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAgentConfig, getOrFetchFeatureFlags]);

  const onChange = useCallback(() => {
    setAvailableDomainsObjects(getDomainsObjects());
  }, [getDomainsObjects]);

  useEffect(() => {
    const unsubscribe = useObjectStore.subscribe(onChange);

    setTimeout(() => {
      void fetch();
    }, 0);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notifyChanged = () => {
    setChanges(true);
  };

  const handleChangeFilteringEnabled = (networkType: NetworkType, event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].filtering_enabled = event.target.checked;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeDefaultFilteringMode = (networkType: NetworkType, input: string) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].default_filtering_mode = input;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeMitmEnabled = (networkType: NetworkType, event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].mitm_enabled = event.target.checked;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeGoogleSafeSearch = (networkType: NetworkType, event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].safe_search.google.enabled = event.target.checked;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeBingSafeSearch = (networkType: NetworkType, event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].safe_search.bing.enabled = event.target.checked;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeYoutubeSafeSearch = (networkType: NetworkType, event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].safe_search.youtube.enabled = event.target.checked;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeYoutubeSafeSearchMode = (networkType: NetworkType, input: string) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].safe_search.youtube.mode = input;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeClassroomEnabled = (networkType: NetworkType, event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = Object.assign({}, networkConfig);
    newState[networkType].classroom_enabled = event.target.checked;
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangeExcludeDomains = (options: Option[]) => {
    setNetworkConfig({ ...networkConfig, exclude_domains: options.map((option) => `${option.value}`) });
    notifyChanged();
  };

  const handleChangedFilteringGroup = (networkType: NetworkType, mode: FilteringMode, groups: Option[]) => {
    setNetworkConfig({
      ...networkConfig,
      [networkType]: {
        ...networkConfig[networkType],
        filtering_groups: {
          ...networkConfig[networkType].filtering_groups,
          [mode]: groups.map((group) => group.name as string),
        },
      },
    });
    notifyChanged();
  };

  const handleModifyInspected = (networkType: NetworkType, obj: MitmObject) => {
    const newState = Object.assign({}, networkConfig);
    if (obj["signatures"]) {
      newState[networkType].mitm_inclusions.signatures = obj["signatures"];
    }
    if (obj["objects"]) {
      newState[networkType].mitm_inclusions.website_objects = obj["objects"];
    }
    if (obj["websites"]) {
      newState[networkType].mitm_inclusions.websites = obj["websites"];
    }
    setNetworkConfig(newState);
    notifyChanged();
  };

  const handleChangedMitmExcludeGroups = (networkType: NetworkType, groups: Option[]) => {
    setNetworkConfig({
      ...networkConfig,
      [networkType]: { ...networkConfig[networkType], mitm_exclude_groups: groups.map((group) => group.name as string) },
    });
    notifyChanged();
  };

  const countMitmInclusions = (mitmInclusions: MitmInclusions) => {
    return mitmInclusions.websites.length + mitmInclusions.signatures.length + mitmInclusions.website_objects.length;
  };

  const validateConfig = () => {
    let updatedOnNetworkConfigValid = true;
    let updatedOffNetworkConfigValid = true;

    /* Check on_network config */
    if (networkConfig.on_network.mitm_enabled && countMitmInclusions(networkConfig.on_network.mitm_inclusions) === 0) {
      updatedOnNetworkConfigValid = false;
    }

    /* Check off_network config */
    if (networkConfig.off_network.mitm_enabled && countMitmInclusions(networkConfig.off_network.mitm_inclusions) === 0) {
      updatedOffNetworkConfigValid = false;
    }

    setOnNetworkConfigValid(updatedOnNetworkConfigValid);
    setOffNetworkConfigValid(updatedOffNetworkConfigValid);

    return updatedOnNetworkConfigValid && updatedOffNetworkConfigValid;
  };

  const ifDomainNotExcludedInCompanion = () => {
    return (
      !(networkConfig.exclude_domains.length > 0) &&
      (networkConfig.off_network.default_filtering_mode === "companion" ||
        networkConfig.off_network.filtering_groups.companion.length > 0 ||
        networkConfig.on_network.default_filtering_mode === "companion" ||
        networkConfig.on_network.filtering_groups.companion.length > 0)
    );
  };

  const handleSaveObject = async (agentConfig: AgentConfig) => {
    setChanges(true);
    setSaving(true);

    try {
      await saveAgentConfig(agentConfig);
      setAgentConfig(agentConfig);
      setChanges(false);
      setSaving(false);
    } catch (error) {
      console.log(error);
      setSaving(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (validateConfig()) {
      handleSaveObject({
        on_network: networkConfig.on_network,
        off_network: networkConfig.off_network,
        exclude_domains: networkConfig.exclude_domains,
        auto_set_policies: networkConfig.auto_set_policies,
      }).catch((error: ResponseError) => {
        console.log(error);
      });
    } else {
      notifyChanged();
    }
  };

  const renderError = (networkType: NetworkType) => {
    if ((networkType === "on_network" && !onNetworkConfigValid) || (networkType === "off_network" && !offNetworkConfigValid)) {
      return <div className={"errorMessage"}>Must select at least one Website/Category</div>;
    }
    return <></>;
  };

  const renderFilteringGroups = (networkType: NetworkType, title: string, mode: FilteringMode): JSX.Element => {
    return (
      <FormControl mb="10px" isDisabled={!loaded || saving}>
        <Flex gap="sp8">
          <FormLabel width="110px" mt="sp12">
            {t(title)}
          </FormLabel>
          <Flex direction="column" width="80%">
            <MultiGroupSearchSelector
              preselected={networkConfig[networkType].filtering_groups[mode].map((group) => ({
                value: group,
                name: group,
              }))}
              onChangeGroups={(e) => handleChangedFilteringGroup(networkType, mode, e)}
              name={`${networkType} ${mode} filtering groups selector`}
              useLegacyId={true}
            />
          </Flex>
        </Flex>
      </FormControl>
    );
  };

  const renderMitmOptions = (networkType: NetworkType): JSX.Element => {
    const allowed_signatures = [
      "sphirewall.application.googlesearch",
      "sphirewall.application.bing",
      "sphirewall.application.youtube",
      "sphirewall.application.vimeo",
      "sphirewall.application.encryptedclienthello",
    ];
    if (networkConfig[networkType].mitm_enabled) {
      const id = `${networkType}_inspected_signatures`;
      return (
        <>
          <FormControl mb="10px" isDisabled={!loaded || saving}>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("Inspected")}
              </FormLabel>
              <Flex direction="column" width="80%">
                <SimpleLayer7CriteriaSelector
                  placeholder={"Search for Website/Category"}
                  allowed_signatures={allowed_signatures}
                  selected_websites={networkConfig[networkType].mitm_inclusions.websites}
                  selected_signatures={networkConfig[networkType].mitm_inclusions.signatures}
                  selected_website_objects={networkConfig[networkType].mitm_inclusions.website_objects}
                  handle_BubbleChanges={(e: MitmObject) => handleModifyInspected(networkType, e)}
                  id={id}
                />
                {renderError(networkType)}
              </Flex>
            </Flex>
          </FormControl>

          <FormControl mb="10px" isDisabled={!loaded || saving}>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("MITM Exclude Groups")}
              </FormLabel>
              <Flex direction="column" width="80%">
                <MultiGroupSearchSelector
                  preselected={
                    networkConfig[networkType].mitm_exclude_groups.map((group) => ({
                      value: group,
                      name: group,
                    })) ?? []
                  }
                  onChangeGroups={(e) => handleChangedMitmExcludeGroups(networkType, e)}
                  name={`${networkType} mitm exclude groups selector`}
                  useLegacyId={true}
                />
              </Flex>
            </Flex>
          </FormControl>
        </>
      );
    }
    return <></>;
  };

  const renderYoutubeOptions = (networkType: NetworkType): JSX.Element => {
    if (networkConfig[networkType].safe_search && networkConfig[networkType].safe_search.youtube.enabled) {
      const valueDataMap: Option[] = [
        { value: "MODERATE", label: "MODERATE" },
        { value: "STRICT", label: "STRICT" },
      ];

      return (
        <Flex gap="sp8">
          <FormLabel width="110px" mt="sp12">
            {t("YouTube Mode")}
          </FormLabel>
          <Flex direction="column" width="80%">
            <SingleSearchSelector
              options={valueDataMap}
              selected={valueDataMap.find((item) => item.value === (networkConfig[networkType].safe_search.youtube.mode || "MODERATE"))}
              onChange={(item) => {
                if (item && item.value) handleChangeYoutubeSafeSearchMode(networkType, `${item.value}`);
              }}
              filterOptions={false}
              showClearIcon={false}
              disabled={!loaded}
              placeholder={t("Select youtube safe search mode")}
              name="youtube-mode-selector"
              aria-label={`${networkType} youtube safe search mode ${networkConfig[networkType].safe_search.youtube.mode || "MODERATE"}`}
            />
          </Flex>
        </Flex>
      );
    }
    return <></>;
  };

  const renderSafeSearchOptions = (networkType: NetworkType): JSX.Element => {
    if (networkConfig[networkType].filtering_enabled && networkConfig[networkType].safe_search) {
      return (
        <>
          <SectionHeading title={t("Safe Search")} aria-level={3} />
          <Flex px="sp12" py="sp16" direction="column" gap="sp16">
            <FormControl mb="10px" isDisabled={!loaded || saving}>
              <Checkbox
                isChecked={networkConfig[networkType].safe_search.google.enabled}
                onChange={(e) => handleChangeGoogleSafeSearch(networkType, e)}
                aria-label={`${networkType} google safe search ${
                  networkConfig[networkType].safe_search.google.enabled ? "enabled" : "disabled"
                }`}
              >
                {t("Google")}
              </Checkbox>
            </FormControl>
            <FormControl mb="10px" isDisabled={!loaded || saving}>
              <Checkbox
                isChecked={networkConfig[networkType].safe_search.bing.enabled}
                onChange={(e) => handleChangeBingSafeSearch(networkType, e)}
                aria-label={`${networkType} bing safe search ${
                  networkConfig[networkType].safe_search.bing.enabled ? "enabled" : "disabled"
                }`}
              >
                {t("Bing")}
              </Checkbox>
            </FormControl>
            <FormControl mb="10px" isDisabled={!loaded || saving}>
              <Checkbox
                isChecked={networkConfig[networkType].safe_search.youtube.enabled}
                onChange={(e) => handleChangeYoutubeSafeSearch(networkType, e)}
                aria-label={`${networkType} youtube safe search ${
                  networkConfig[networkType].safe_search.youtube.enabled ? "enabled" : "disabled"
                }`}
              >
                {t("YouTube")}
              </Checkbox>
              {renderYoutubeOptions(networkType)}
            </FormControl>
          </Flex>
        </>
      );
    }
    return <></>;
  };

  const renderOptions = (networkType: NetworkType): JSX.Element => {
    if (networkConfig[networkType].filtering_enabled) {
      const valueDataMap: Option[] = [
        { value: "dns", label: "DNS" },
        { value: "web", label: "WEB" },
        { value: "companion", label: "COMPANION" },
      ];

      //Full mode is only visible for support admins or its already enabled
      const shouldEnableFullMode = supportAdmin || networkConfig[networkType].default_filtering_mode === "full";
      if (shouldEnableFullMode) {
        valueDataMap.push({ value: "full", label: "FULL" });
      }

      return (
        <>
          <FormControl mb="10px" isDisabled={!loaded || saving}>
            <Flex gap="sp8">
              <FormLabel width="110px" mt="sp12">
                {t("Default Filtering Mode")}
              </FormLabel>
              <Flex direction="column" width="80%">
                <SingleSearchSelector
                  options={valueDataMap}
                  selected={valueDataMap.find((item) => item.value === networkConfig[networkType].default_filtering_mode)}
                  onChange={(item) => {
                    if (item && item.value) handleChangeDefaultFilteringMode(networkType, `${item.value}`);
                  }}
                  filterOptions={false}
                  showClearIcon={false}
                  disabled={!loaded}
                  placeholder={t("Select Default Filtering Mode")}
                  name="default-filtering-mode-selector"
                  aria-label={`${networkType} default filtering mode ${networkConfig[networkType].default_filtering_mode}`}
                />
              </Flex>
            </Flex>
          </FormControl>

          {featureFlags &&
            featureFlags.config_agents_filtering_bypass &&
            renderFilteringGroups(networkType, "BYPASS Filtering Groups", "bypass")}

          {renderFilteringGroups(networkType, "DNS Filtering Groups", "dns")}
          {renderFilteringGroups(networkType, "WEB Filtering Groups", "web")}
          {renderFilteringGroups(networkType, "COMPANION Filtering Groups", "companion")}

          {shouldEnableFullMode && renderFilteringGroups(networkType, "FULL Filtering Groups", "full")}

          <FormControl mb="10px" isDisabled={!loaded || saving}>
            <Checkbox
              isChecked={networkConfig[networkType].mitm_enabled}
              onChange={(e) => handleChangeMitmEnabled(networkType, e)}
              aria-label={`${networkType} mitm ${networkConfig[networkType].mitm_enabled ? "enabled" : "disabled"}`}
            >
              {t("MITM Enabled")}
            </Checkbox>
          </FormControl>

          {renderMitmOptions(networkType)}
        </>
      );
    }

    return <></>;
  };

  const renderClassroomConfig = (networkType: NetworkType): JSX.Element => {
    return (
      <>
        <SectionHeading title={t("Classroom Manager")} aria-level={3} />
        <Flex px="sp12" py="sp16" direction="column" gap="sp16">
          <FormControl mb="10px" isDisabled={!loaded || saving}>
            <Checkbox
              isChecked={networkConfig[networkType].classroom_enabled}
              onChange={(e) => handleChangeClassroomEnabled(networkType, e)}
              aria-label={`${networkType} classroom ${networkConfig[networkType].classroom_enabled ? "enabled" : "disabled"}`}
            >
              {t("Classroom Enabled")}
            </Checkbox>
          </FormControl>
        </Flex>
      </>
    );
  };

  const renderExcludeDomains = (): JSX.Element => {
    const options = availableDomainsObjects.map((item) => ({
      value: item.id || "",
      label: item.name,
    }));

    const selectedOptions = networkConfig.exclude_domains.map((item) => ({
      value: item,
      label: availableDomainsObjects.find((domain) => domain.id === item)?.name || item, // Fallback to item if not found
    }));

    return (
      <FormControl mb="10px" isDisabled={!loaded || saving}>
        <Flex gap="sp8">
          <FormLabel width="110px" mt="sp12">
            {t("Exclude Domains")}
          </FormLabel>
          <Flex direction="column" width="80%" gap="sp8">
            <MultiSearchSelector
              name="domain-search-selector"
              placeholder={t("Search Domain Objects")}
              selected={selectedOptions}
              options={options}
              onChange={handleChangeExcludeDomains}
              filterOptions={false}
            />
            {ifDomainNotExcludedInCompanion() && (
              <InlineNotification
                status="error"
                notificationDescription={
                  <>
                    Companion Mode will only work if you{" "}
                    <Link textDecoration="underline" isExternal={true} href="https://help.linewize.com/hc/en-gb/articles/5766639283228">
                      exclude internal DNS domains
                    </Link>
                  </>
                }
              />
            )}
          </Flex>
        </Flex>
      </FormControl>
    );
  };

  const renderNetworkConfiguration = (networkType: NetworkType, title: string): JSX.Element => {
    return (
      <Box p="sp24">
        <SectionHeading title={t(title)} aria-level={2} />
        <Flex px="sp12" py="sp16" direction="column" gap="sp16">
          <FormControl mb="10px" isDisabled={!loaded || saving}>
            <Checkbox
              isChecked={networkConfig[networkType].filtering_enabled}
              onChange={(e) => handleChangeFilteringEnabled(networkType, e)}
              aria-label={`${networkType} filtering ${networkConfig[networkType].filtering_enabled ? "enabled" : "disabled"}`}
            >
              {t("Filtering Enabled")}
            </Checkbox>
          </FormControl>
          {renderOptions(networkType)}
        </Flex>
        {renderSafeSearchOptions(networkType)}
        {renderClassroomConfig(networkType)}
      </Box>
    );
  };

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Mobile Agent"), isActive: true },
  ];

  return (
    <Box flexDirection="column" minWidth="100%">
      <CardBasedPage title={t("Mobile Agent")} breadcrumbs={breadcrumbs}>
        {renderNetworkConfiguration("on_network", "On Linewize Filter Network")}
      </CardBasedPage>
      <CardBasedPage topMargin="0">{renderNetworkConfiguration("off_network", "Off Linewize Filter Network")}</CardBasedPage>
      <CardBasedPage topMargin="0">
        <Box p="sp24">
          <SectionHeading title={t("DNS Filtering")} aria-level={2} />
          <Flex px="sp12" py="sp16" direction="column" gap="sp16">
            {renderExcludeDomains()}
          </Flex>
        </Box>
        <Box p="sp24" backgroundColor="brand.100" borderBottomRadius="sm">
          <Button mt="sp12" variant="primary" onClick={handleSubmit} isDisabled={!loaded || saving || !changes}>
            {saving ? t("Saving..") : changes ? t("Save") : t("Saved")}
          </Button>
        </Box>
      </CardBasedPage>
    </Box>
  );
};

export default MobileAgentConfiguration;
